import { useState, useEffect, useCallback } from 'react';
import { History, ArrowDownLeft, ArrowUpRight, Search, Filter, Download, RefreshCw } from 'lucide-react';
import { PageShell, fmtINR, fmtDT, Spinner, useBanner, useWalletUser, EmptyState } from './WalletShared';
import walletController from '../../controllers/walletController';

const TXN_TYPE_CFG = {
  credit:    { color:'#15803d', bg:'#dcfce7', label:'Credit',    Icon:ArrowDownLeft  },
  debit:     { color:'#b91c1c', bg:'#fef2f2', label:'Debit',     Icon:ArrowUpRight   },
  refund:    { color:'#6d28d9', bg:'#ede9fe', label:'Refund',    Icon:ArrowDownLeft  },
  cashback:  { color:'#0f6e56', bg:'#e1f5ee', label:'Cashback',  Icon:ArrowDownLeft  },
};

const Transactions = () => {
  const { userId } = useWalletUser();
  const { banner, notify, clearBanner } = useBanner();
  const [txns,       setTxns]       = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [pagination, setPagination] = useState({ page:1, pages:1, total:0 });
  const [filters,    setFilters]    = useState({ page:1, limit:15, type:'', sortOrder:'desc' });
  const [search,     setSearch]     = useState('');

  const load = useCallback(async (silent=false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    const f = { ...filters };
    if (search) f.search = search;
    const res = await walletController.getTransactions(userId, f);
    if (res.success) { setTxns(res.data); setPagination(res.pagination||{}); }
    else notify('error', res.message);
    setLoading(false); setRefreshing(false);
  }, [userId, filters, search]);

  useEffect(() => { load(false); }, [load]);

  const setFilter = (k,v) => setFilters(p => ({ ...p, [k]:v, page:1 }));

  return (
    <PageShell title="Transactions" subtitle="Complete transaction history" icon={History}
      banner={banner} onCloseBanner={clearBanner}
      actions={
        <button onClick={() => load(true)} disabled={refreshing} style={{ display:'flex', alignItems:'center', gap:5, padding:'7px 12px', background:'#fff', border:'1px solid #e2e8f0', borderRadius:8, fontSize:12, cursor:'pointer', color:'#475569' }}>
          <RefreshCw size={13} style={{ animation:refreshing?'spin 1s linear infinite':'none' }}/> Refresh
        </button>
      }>

      {/* Filters */}
      <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap' }}>
        <div style={{ position:'relative', flex:1, minWidth:180 }}>
          <Search size={14} color="#94a3b8" style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)' }}/>
          <input value={search} onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key==='Enter' && load(false)}
            placeholder="Search transactions…"
            style={{ width:'100%', padding:'8px 8px 8px 32px', border:'1px solid #e2e8f0', borderRadius:8, fontSize:13, outline:'none', boxSizing:'border-box', background:'#fff' }}/>
        </div>
        <select value={filters.type} onChange={e => setFilter('type',e.target.value)}
          style={{ padding:'8px 10px', border:'1px solid #e2e8f0', borderRadius:8, fontSize:13, background:'#fff', outline:'none' }}>
          <option value="">All Types</option>
          {Object.entries(TXN_TYPE_CFG).map(([v,c]) => <option key={v} value={v}>{c.label}</option>)}
        </select>
        <select value={filters.sortOrder} onChange={e => setFilter('sortOrder',e.target.value)}
          style={{ padding:'8px 10px', border:'1px solid #e2e8f0', borderRadius:8, fontSize:13, background:'#fff', outline:'none' }}>
          <option value="desc">Newest first</option>
          <option value="asc">Oldest first</option>
        </select>
      </div>

      {loading
        ? <div style={{ display:'flex', justifyContent:'center', alignItems:'center', minHeight:300, gap:10, color:'#64748b' }}><Spinner size={26}/><span>Loading…</span></div>
        : txns.length===0
          ? <EmptyState icon={History} message="No transactions found"/>
          : (
            <div style={{ background:'#fff', borderRadius:12, border:'1px solid #f1f5f9', overflow:'hidden' }}>
              {txns.map((t,i) => {
                const cfg = TXN_TYPE_CFG[t.type] || TXN_TYPE_CFG.debit;
                const Icon = cfg.Icon;
                return (
                  <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'13px 18px', borderBottom:'1px solid #f8fafc', transition:'background .1s' }}
                    onMouseEnter={e => e.currentTarget.style.background='#f8fafc'}
                    onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                    <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                      <div style={{ width:38, height:38, borderRadius:10, background:cfg.bg, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                        <Icon size={16} color={cfg.color}/>
                      </div>
                      <div>
                        <div style={{ fontSize:13, fontWeight:500, color:'#1e293b' }}>{t.description||t.type}</div>
                        <div style={{ fontSize:11, color:'#94a3b8', display:'flex', gap:8 }}>
                          <span>{fmtDT(t.createdAt)}</span>
                          {t.referenceId && <span style={{ fontFamily:'monospace' }}>#{t.referenceId}</span>}
                        </div>
                      </div>
                    </div>
                    <div style={{ textAlign:'right' }}>
                      <div style={{ fontSize:15, fontWeight:700, color:cfg.color }}>
                        {t.type==='credit'||t.type==='refund'||t.type==='cashback' ? '+' : '-'}{fmtINR(t.amount)}
                      </div>
                      <span style={{ fontSize:10, padding:'2px 7px', borderRadius:5, background:cfg.bg, color:cfg.color, fontWeight:600 }}>{cfg.label}</span>
                    </div>
                  </div>
                );
              })}

              {/* Pagination */}
              {pagination.pages>1 && (
                <div style={{ padding:'12px 18px', borderTop:'1px solid #f1f5f9', display:'flex', justifyContent:'space-between', alignItems:'center', fontSize:13 }}>
                  <span style={{ color:'#64748b' }}>Total: {pagination.total} transactions</span>
                  <div style={{ display:'flex', gap:4 }}>
                    <button disabled={pagination.page<=1} onClick={() => setFilter('page', pagination.page-1)}
                      style={{ padding:'5px 10px', border:'1px solid #e2e8f0', borderRadius:6, background:'#fff', cursor:'pointer', fontSize:12 }}>← Prev</button>
                    <button disabled={pagination.page>=pagination.pages} onClick={() => setFilter('page', pagination.page+1)}
                      style={{ padding:'5px 10px', border:'1px solid #e2e8f0', borderRadius:6, background:'#fff', cursor:'pointer', fontSize:12 }}>Next →</button>
                  </div>
                </div>
              )}
            </div>
          )}
    </PageShell>
  );
};
export default Transactions;